/**
 * Design Method Toggle System
 * Allows users to switch between file upload and PitchPrint design methods
 * Ensures clean state transitions and prevents path conflicts
 */
(() => {
  'use strict';

  const log = (...args) => window.PITCHPRINT_DEBUG && console.log('[DesignMethodToggle]', ...args);

  // Wait for StateManager to be available
  const initToggle = () => {
    if (!window.PitchPrintStateManager) {
      log('⏳ Waiting for StateManager...');
      setTimeout(initToggle, 100);
      return;
    }

    const stateManager = window.PitchPrintStateManager;
    const toggle = document.getElementById('design-method-toggle');

    if (!toggle) {
      log('❌ Toggle element not found (product may not have both systems enabled)');
      return;
    }

    const radioButtons = toggle.querySelectorAll('input[type="radio"][name="design_method"]');
    if (radioButtons.length === 0) {
      log('❌ No radio buttons found in toggle');
      return;
    }

    log('✅ Design method toggle initialized');

    // Get initial method from StateManager or default to pitchprint
    let currentMethod = stateManager.getDesignMethod() || 'pitchprint';

    // Set initial radio selection
    radioButtons.forEach(radio => {
      if (radio.value === currentMethod) {
        radio.checked = true;
      }
    });

    // Update visibility based on initial method
    updateSystemVisibility(currentMethod);

    // Persist default to StateManager so guards in other systems (e.g. upload mode handler) work correctly
    if (!stateManager.getDesignMethod()) {
      stateManager.setDesignMethod(currentMethod);
    }

    log(`🎯 Initial design method: ${currentMethod}`);

    // Handle toggle changes
    radioButtons.forEach(radio => {
      radio.addEventListener('change', (e) => {
        if (!e.target.checked) return;

        const newMethod = e.target.value;
        const oldMethod = currentMethod;

        if (newMethod === oldMethod) {
          log(`✅ Already on ${newMethod} method, no change needed`);
          return;
        }

        log(`🔄 Switching design method: ${oldMethod} → ${newMethod}`);

        // Note: Confirmation removed for better UX flow
        // User can always switch back if needed
        // Previous method's data will be automatically cleared

        // Update state
        currentMethod = newMethod;
        stateManager.setDesignMethod(newMethod);

        // Dispatch DOM event for analytics consumption only — separates real
        // user interactions from init-time setDesignMethod calls (LEAK-B-09).
        document.dispatchEvent(new CustomEvent('design:method:user-changed', { detail: newMethod }));

        // Clear the opposite method's data
        clearOppositeMethodData(newMethod);

        // Update system visibility
        updateSystemVisibility(newMethod);

        log(`✅ Switched to ${newMethod} method`);
      });
    });

    // Helper: Clear data from the opposite method
    function clearOppositeMethodData(activeMethod) {
      if (activeMethod === 'pitchprint') {
        // Clear file upload data
        log('🧹 Clearing file upload data');
        stateManager.clearUploadData();

        // Reset file uploader UI
        if (window.FileUploader && typeof window.FileUploader.reset === 'function') {
          window.FileUploader.reset();
          log('✅ File uploader UI reset');
        }
      } else if (activeMethod === 'file_upload') {
        // Clear PitchPrint project data
        log('🧹 Clearing PitchPrint project data');
        stateManager.clearProject();

        // Try to clear PitchPrint editor if available
        if (window.ppclient && window.ppclient.fire) {
          try {
            window.ppclient.fire('clear-design');
            log('✅ PitchPrint editor cleared');
          } catch (error) {
            log('⚠️ Could not clear PitchPrint editor:', error);
          }
        }
      }
    }

    // Helper: Show/hide systems based on active method
    function updateSystemVisibility(activeMethod) {
      const fileUploadContainer = document.getElementById('custom-file-uploader-container');

      // Target the actual PitchPrint button container that PitchPrint creates
      const pitchprintButtons = document.getElementById('pp_main_btn_sec');

      // Also hide the wrapper containers if they exist
      const pitchprintWrappers = document.querySelectorAll('.new-design-button-here, .thumb-btn-wrapper');

      // Hide the entire pitchprint-section container (includes both collection-preview-mode and standard-mode)
      const pitchprintSection = document.querySelector('.pitchprint-section');

      // Hide confidence builder when file upload is active
      const confidenceBuilder = document.querySelector('.step2-confidence-builder');

      log(`👁️ Updating visibility for method: ${activeMethod}`);

      if (activeMethod === 'file_upload') {
        // Show file upload, hide PitchPrint buttons
        if (fileUploadContainer) {
          fileUploadContainer.style.display = 'block';
          log('✅ File upload container shown');
        }

        // Hide the actual PitchPrint button container
        if (pitchprintButtons) {
          pitchprintButtons.style.display = 'none';
          log('🙈 PitchPrint buttons (#pp_main_btn_sec) hidden');
        }

        // Hide the pitchprint-section container
        if (pitchprintSection) {
          pitchprintSection.style.display = 'none';
          log('🙈 PitchPrint section container hidden');
        }

        // Also hide wrapper containers
        pitchprintWrappers.forEach(wrapper => {
          wrapper.style.display = 'none';
          log(`🙈 PitchPrint wrapper hidden: ${wrapper.id || wrapper.className}`);
        });

        // Hide confidence builder
        if (confidenceBuilder) {
          confidenceBuilder.style.display = 'none';
          log('🙈 Confidence builder hidden');
        }
      } else if (activeMethod === 'pitchprint') {
        // Show PitchPrint buttons, hide file upload
        if (fileUploadContainer) {
          fileUploadContainer.style.display = 'none';
          log('🙈 File upload container hidden');
        }

        // Show the actual PitchPrint button container
        if (pitchprintButtons) {
          pitchprintButtons.style.display = 'block';
          log('✅ PitchPrint buttons (#pp_main_btn_sec) shown');
        }

        // Show the pitchprint-section container
        if (pitchprintSection) {
          pitchprintSection.style.display = 'block';
          log('✅ PitchPrint section container shown');
        }

        // Also show wrapper containers
        pitchprintWrappers.forEach(wrapper => {
          wrapper.style.display = 'flex';
          log(`✅ PitchPrint wrapper shown: ${wrapper.id || wrapper.className}`);
        });

        // Show confidence builder
        if (confidenceBuilder) {
          confidenceBuilder.style.display = 'block';
          log('✅ Confidence builder shown');
        }
      }
    }

    // Listen for StateManager events to keep toggle in sync
    stateManager.on('design:method:changed', (event) => {
      const method = event.detail;
      log(`📡 StateManager design method changed: ${method}`);

      // Update radio selection
      radioButtons.forEach(radio => {
        radio.checked = (radio.value === method);
      });

      // Update visibility
      updateSystemVisibility(method);
    });

    // Expose public API
    window.DesignMethodToggle = {
      getCurrentMethod: () => currentMethod,
      setMethod: (method) => {
        const radio = toggle.querySelector(`input[value="${method}"]`);
        if (radio) {
          radio.click();
        }
      },
      refresh: () => {
        updateSystemVisibility(currentMethod);
      }
    };

    log('✅ Design method toggle system ready');
  };

  // Initialize when DOM is ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initToggle);
  } else {
    initToggle();
  }

  log('📦 Design method toggle script loaded');
})();
